import { firstValueFrom } from 'rxjs';
import {
  Component,
  WritableSignal,
  OnInit,
  ChangeDetectorRef,
  signal,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { CourierEnquiryDto, CourierProviderDto, ProjectDto, VehicleDto } from '../../shared/generated/backend';
import {
  DxButtonModule,
  DxDataGridModule,
  DxLoadIndicatorModule,
  DxLoadPanelModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextAreaModule,
} from 'devextreme-angular';
import { SharedModule } from '../../shared/shared.module';
import { DxoEditingModule } from 'devextreme-angular/ui/nested';
import { DataChange, RowUpdatingEvent } from 'devextreme/ui/data_grid';
import { FleetService, ProjectService, UpdateProjectData } from '../../shared/generated/backend';
import notify from 'devextreme/ui/notify';
import { TranslateService } from '@ngx-translate/core';
import { ValueChangedEvent } from 'devextreme/ui/text_area';
import { DxDataGridComponent } from 'devextreme-angular';
import { createBusyState } from '../../shared/util/busy-state';

@Component({
  selector: 'app-project-courier',
  imports: [
    SharedModule,
    DxTagBoxModule,
    DxButtonModule,
    DxDataGridModule,
    DxoEditingModule,
    DxLoadPanelModule,
    DxLoadIndicatorModule,
    DxTextAreaModule,
    DxPopupModule,
    DxSelectBoxModule,
  ],
  templateUrl: './project-courier.component.html',
  styleUrl: './project-courier.component.scss',
})
export class ProjectCourierComponent implements OnInit {
  @ViewChild('grid', { static: false })
  grid!: DxDataGridComponent;
  readonly project = input.required<WritableSignal<ProjectDto>>();
  readonly changed = output<string>();

  selectedCouriers: number[] = [];
  courierSelection: CourierProviderDto[] = [];

  courierEnqueries: WritableSignal<{ Provider: CourierProviderDto; Enquiry: CourierEnquiryDto }[]> = signal([]);

  isLoading: boolean = true;
  // Guards sending and accepting enquiries against double triggering.
  readonly enquiryBusy = createBusyState();
  enquiryMessage: string = '';
  confirmationMessage: string = '';
  changes: DataChange[] = [];
  tempOfferDate: WritableSignal<Date | null> = signal(null);
  enquiryCompleted: boolean = false;

  popupVisible: boolean = false;
  courierVehicles: WritableSignal<VehicleDto[]> = signal([]);
  selectedCourierVehicle: VehicleDto | undefined;

  private fleetService = inject(FleetService);
  private projectService = inject(ProjectService);
  private translationService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  async ngOnInit() {
    // get courier providers
    this.courierSelection = await firstValueFrom(this.fleetService.getCourierProviders());

    this.getEnqueries();
  }

  allowUpdating = () => !this.isLoading && !this.enquiryCompleted && !this.enquiryBusy.busy();

  getEnqueries() {
    // load existing enquiries
    this.isLoading = true;
    this.courierEnqueries.set([]);
    this.projectService.getCourierEnquiries(this.project()().ProjectNo).subscribe({
      next: async enquiries => {
        //console.log('Enquiries', enquiries);
        const updatedEnquiries = enquiries
          .map(enquiry => {
            const provider = this.courierSelection.find(p => p.AddressNumber === enquiry.ProviderAddressNumber);
            if (provider) {
              // this.courierSelection = this.courierSelection.filter(p => p.Id !== provider.Id);
              return { Provider: provider, Enquiry: enquiry };
            }
            return null;
          })
          .filter(e => e !== null) as { Provider: CourierProviderDto; Enquiry: CourierEnquiryDto }[];

        this.courierEnqueries.set(updatedEnquiries);
        this.cdr.detectChanges();
        this.grid.instance.refresh();
        this.enquiryCompleted = enquiries.some(e => e.ConfirmationSent || e.CancellationSent);
        this.isLoading = false;
      },
      error: this.errorHandling,
    });
  }

  getLocalISOTimeString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    const ms = date.getMilliseconds();

    const base = `${year}-${month}-${day}T${hour}:${minute}:${second}${
      ms > 0 ? '.' + String(ms).padStart(3, '0') : ''
    }`;

    // local offset in HH:MM
    const offset = -date.getTimezoneOffset(); // in minutes; negative if running behind UTC
    const sign = offset >= 0 ? '+' : '-';
    const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
    const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');

    return `${base}${sign}${offsetHours}:${offsetMinutes}`;
  }

  async createEnqueries() {
    await this.enquiryBusy.run(async () => {
      const providers = this.selectedCouriers
        .map(id => this.courierSelection.find(p => p.Id === id))
        .filter((provider): provider is CourierProviderDto => provider !== undefined);
      if (providers.length === 0) return;

      this.selectedCouriers = [];
      this.isLoading = true;
      const requests = providers.map(provider => {
        //enquiryDate: new Date().toISOString()
        const enquiry: CourierEnquiryDto = {
          Id: -1,
          ProjectNo: this.project()().ProjectNo,
          EnquiryDate: this.getLocalISOTimeString(new Date()),
          OfferNetAmount: 0,
          OfferReceived: false,
          Priority: provider.Priority ?? 0,
          ProviderAddressNumber: provider.AddressNumber,
          Comment: '',
          ConfirmationSent: false,
          CancellationSent: false,
        };
        // console.log('Create Enquiry', enquiry);
        return firstValueFrom(
          this.projectService.createCourierEnquiry(this.project()().ProjectNo, {
            courierEnquiryDto: enquiry,
            enqueryMessage: this.enquiryMessage,
          })
        );
      });

      // wait for all requests, so isLoading is not reset by the first one that finishes
      const results = await Promise.allSettled(requests);
      const failed = results.find(result => result.status === 'rejected');
      if (failed) {
        this.errorHandling(failed.reason);
      }
      if (results.some(result => result.status === 'fulfilled')) {
        this.enquiryMessage = '';
      }
      // reload
      this.getEnqueries();
    });
  }

  updateEnquery(e: RowUpdatingEvent) {
    // console.log('Update Enquery', e);
    if (!e.oldData) {
      console.error('No update data', e);
      return;
    }
    const updatedData = { ...e.oldData.Enquiry, ...e.newData?.Enquiry } as CourierEnquiryDto;
    updatedData.OfferNetAmount = updatedData.OfferNetAmount ?? 0; // ensure there is a value present
    this.isLoading = true;
    this.projectService.updateCourierEnquiry(this.project()().ProjectNo, updatedData).subscribe({
      next: () => {
        this.getEnqueries();
        this.tempOfferDate.set(null);
      },
      error: this.errorHandling,
    });
  }

  acceptEnquiry = async (id: number) => {
    if (this.enquiryBusy.busy()) return;
    if (confirm(this.translationService.instant('COURIER.ASK_ACCEPT_ENQUIRY'))) {
      await this.enquiryBusy.run(async () => {
        this.isLoading = true;

        // click the save button in the edit form
        const element = document.querySelector(
          'div.dx-datagrid-form-buttons-container > div:nth-child(1)'
        ) as HTMLElement;
        // console.log('Element', element);
        element?.click();

        // get courier vehivcles
        this.getCourierVehicles();

        try {
          await firstValueFrom(
            this.projectService.acceptCourierEnquiry(this.project()().ProjectNo, {
              id: id,
              confirmationMessage: this.confirmationMessage,
            })
          );
          notify({ message: this.translationService.instant('COURIER.ENQUIRY_ACCEPTED') }, 'success', 5000);
          this.confirmationMessage = '';
          this.isLoading = false;
          this.popupVisible = true;
        } catch (err) {
          this.errorHandling(err);
        }
      });
    }
  };

  finishCourierEnquiry = () => {
    // console.log('Finish Courier Enquiry', this.selectedCourierVehicle);
    this.isLoading = true;
    this.popupVisible = false;

    // TODO: this data might be fixed and not project dependent (e.g. rampNeeded: always false)
    const updateData: UpdateProjectData = {
      ProcessingState: this.project()().ProcessingState!,
      NotificationSend: this.project()().NotificationSend!,
      FromDateTime: this.project()().FromDateTime,
      UntilDateTime: this.project()().UntilDateTime,
      VehicleId: this.selectedCourierVehicle?.vehicleId ?? 0, // TODO: this can be null
      DriverId: this.project()().DriverId ?? 0,
      Priority: this.project()().Priority,
      RampNeeded: this.project()().RampNeeded,
      TunnelCode: this.project()().TunnelCode ? 1 : 0,
      ProjectText: this.project()().ProjectText,
      DriverText: this.project()().DriverText,
      StockText: this.project()().StockText,
      Administrator: this.project()().Administrator,
    };
    this.projectService.updateProject(this.project()().ProjectNo, updateData).subscribe({
      next: () => {
        this.changed.emit('reload'); // trigger a project reload
        this.getEnqueries();
      },
      error: this.errorHandling,
    });
  };

  updateOfferDate = (changes: DataChange[]) => {
    if (!changes[0] || !changes[0].data) return;

    const oldEnquiry = changes[0].key.Enquiry;
    const newPartial = changes[0].data.Enquiry || {};

    const newOfferReceived =
      typeof newPartial.OfferReceived !== 'undefined' ? newPartial.OfferReceived : oldEnquiry.OfferReceived;

    let newOfferDate = typeof newPartial.OfferDate !== 'undefined' ? newPartial.OfferDate : oldEnquiry.OfferDate;

    if (newOfferReceived === false) {
      newOfferDate = null;
    } else if (newOfferReceived === true && !newOfferDate) {
      newOfferDate = this.getLocalISOTimeString(new Date());
    }

    changes[0].data.Enquiry.OfferReceived = newOfferReceived;
    changes[0].data.Enquiry.OfferDate = newOfferDate;
    this.tempOfferDate.set(newOfferDate);

    this.changes = changes;
    this.cdr.detectChanges();
  };

  setConfirmationMessage = ({ value }: ValueChangedEvent) => {
    this.confirmationMessage = value;
  };

  providerWithCityDisplay = (data: { Provider: CourierProviderDto; Enquiry: CourierEnquiryDto }) => {
    return data?.Provider?.Identifier + '\r\n (' + data?.Provider?.City + ')';
  };

  commentDisplay = (data: { Provider: CourierProviderDto; Enquiry: CourierEnquiryDto }) => {
    return data?.Enquiry?.Comment == null ? false : true;
  };

  getCourierVehicles() {
    // console.log('Get Courier Vehicles');
    this.fleetService.getCourierVehicles().subscribe({
      next: vehicles => {
        // console.log('Vehicles', vehicles);
        this.courierVehicles.set(vehicles); // only external vehicles
      },
      error: this.errorHandling,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private errorHandling = (err: any) => {
    console.error('Error', err);
    this.isLoading = false;
    let message: string = 'ERROR.GENERAL';
    if ('error' in err && typeof err.error === 'string') {
      message = 'COURIER.ERROR.' + err.error;
    }
    notify({ message: this.translationService.instant(message), shading: false }, 'error', 5000);
  };
}
